<script setup lang="ts">
import { computed, onUnmounted, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useDataRefreshRuntime } from '@/app/data-refresh';
import {
  chunkLoadingErrorState,
  reloadAfterChunkLoadingError,
} from '@/app/providers/router/chunk-loading-error-handler';
import { usePwaRuntime } from '@/app/pwa';
import { useSession } from '@/features/session';
import { isPlatformHost } from '@/shared/lib';
import { useAppVersion } from '@/shared/lib/app-version';
import { usePageReload } from '@/shared/lib/composables';
import AppVersionNotification from '@/shared/ui/AppVersionNotification.vue';
import { AppButton } from '@/shared/ui/button';
import ConfirmModal from '@/shared/ui/ConfirmModal.vue';
import MaintenanceScreen from '@/shared/ui/MaintenanceScreen.vue';
import { AppSpinner } from '@/shared/ui/spinner';

const appBuildTime = computed(() => {
  return __APP_BUILD_TIME__;
});

usePwaRuntime({
  pushEnabled: !isPlatformHost(),
});
useDataRefreshRuntime();

const { hasNewVersion, isForced, reloadApp, dismiss, updateState, maintenanceActionLost } = useAppVersion();
const route = useRoute();
const router = useRouter();
const { status: sessionStatus, error: sessionError, initializeSession } = useSession();
const isRouterReady = shallowRef(router.currentRoute.value.matched.length > 0);

const removeRouterReadyHook = router.afterEach((_to, _from, failure) => {
  if (!failure) {
    isRouterReady.value = true;
  }
});

onUnmounted(removeRouterReadyHook);

const requiresSession = computed(() => route.meta.requiresAuth ?? true);
// Сессия, отклонённая из-за обязательного обновления, — не ошибка сервиса: под экраном обновления
// показываем загрузку, а не «Повторить».
const isSessionFailed = computed(() => requiresSession.value && sessionStatus.value === 'error');
const hasSessionError = computed(() => isSessionFailed.value && updateState.value === 'ready');
const isSessionLoading = computed(() => {
  return (
    !isRouterReady.value ||
    (requiresSession.value && sessionStatus.value === 'initializing') ||
    (isSessionFailed.value && !hasSessionError.value)
  );
});
const sessionErrorMessage = computed(() => {
  return sessionError.value?.message ?? 'Не удалось загрузить данные сессии. Повторите попытку.';
});

const retrySession = async (): Promise<void> => {
  await initializeSession();

  if (sessionStatus.value === 'ready') {
    const requestedLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    await router.replace(requestedLocation);
  }
};

// Ключ мягкой перезагрузки: смена пересоздаёт активную страницу (клик по уже
// активному пункту меню). Не завязан на route — обычная навигация и смена query
// не трогают ключ, поэтому состояние страницы при выборе диалога сохраняется.
const { reloadKey } = usePageReload();
</script>

<template>
  <MaintenanceScreen
    v-if="updateState === 'maintenance'"
    :action-lost="maintenanceActionLost"
  />
  <!-- Блокирует сразу, а показывается через 300 мс: быстрая перезагрузка не мелькает экраном. -->
  <div
    v-else-if="updateState !== 'ready'"
    class="fixed inset-0 z-(--toast-z-index) flex items-center justify-center bg-black/50 p-4 transition-opacity delay-300 duration-200 motion-reduce:transition-none starting:opacity-0"
    role="alert"
  >
    <section
      class="flex w-full max-w-sm items-start gap-3 rounded-xl border border-(--base-popup-border-color) bg-(--base-popup-bg) p-4 text-(--base-popup-text-color) shadow-lg"
    >
      <AppSpinner
        v-if="updateState === 'updating'"
        size="sm"
        class="mt-0.5 shrink-0"
      />
      <div class="flex flex-col gap-1">
        <p class="font-medium">
          {{ updateState === 'failed' ? 'Не получилось обновить' : 'Загружаем новую версию' }}
        </p>
        <p class="text-sm text-(--base-popup-secondary-color)">
          {{
            updateState === 'failed'
              ? 'Обновите страницу. Если не поможет, закройте вкладку и откройте Морс снова.'
              : 'Это займёт несколько секунд.'
          }}
        </p>
        <AppButton
          v-if="updateState === 'failed'"
          class="mt-2 min-h-11 self-start"
          @click="reloadApp"
          >Обновить страницу</AppButton
        >
      </div>
    </section>
  </div>
  <div
    class="min-h-screen bg-(--base-main-bg) text-(--base-text-color)"
    :inert="updateState !== 'ready'"
  >
    <AppVersionNotification
      :is-visible="hasNewVersion"
      :is-forced="isForced"
      @reload="reloadApp"
      @dismiss="dismiss"
    />
    <ConfirmModal />

    <div
      v-if="chunkLoadingErrorState"
      role="alert"
      class="flex min-h-screen items-center justify-center p-4"
    >
      <div class="flex max-w-md flex-col items-center gap-4 rounded-xl bg-(--base-container-bg) p-4 text-center md:p-6">
        <p>{{ chunkLoadingErrorState.message }}</p>
        <AppButton
          class="min-h-11"
          @click="reloadAfterChunkLoadingError"
        >
          Обновить страницу
        </AppButton>
      </div>
    </div>

    <div
      v-else-if="hasSessionError"
      role="alert"
      class="flex min-h-screen items-center justify-center p-4"
    >
      <div class="flex max-w-md flex-col items-center gap-4 rounded-xl bg-(--base-container-bg) p-4 text-center md:p-6">
        <p>{{ sessionErrorMessage }}</p>
        <AppButton
          class="min-h-11"
          @click="retrySession"
        >
          Повторить
        </AppButton>
      </div>
    </div>

    <div
      v-else-if="isSessionLoading"
      role="status"
      aria-live="polite"
      class="flex min-h-screen items-center justify-center gap-3"
    >
      <AppSpinner
        aria-hidden="true"
        size="lg"
      />
      <span class="sr-only">Загрузка приложения</span>
    </div>

    <router-view
      v-else
      v-slot="{ Component }"
    >
      <Suspense>
        <component
          :is="Component"
          :key="reloadKey"
        />

        <template #fallback>
          <div
            role="status"
            aria-live="polite"
            class="flex min-h-screen items-center justify-center gap-3"
          >
            <AppSpinner
              aria-hidden="true"
              size="lg"
            />
            <span class="sr-only">Загрузка страницы</span>
          </div>
        </template>
      </Suspense>
    </router-view>

    <div class="mt-4 ml-4 hidden text-xs text-gray-400">Last builded at: {{ appBuildTime }}</div>
  </div>
</template>
